// js/auth.js

import supabase from './supabase-config.js';
import { showConfirmationModal } from './utils/modal.js';

const auth = {
    /**
     * Tenta registrar um novo usuário no Supabase Auth e, em seguida,
     * atualiza o perfil correspondente criado por um gatilho no banco de dados.
     * @param {object} userData - Dados do usuário (email, password, username, etc.).
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async signUp(userData) {
        // 1. Cria o usuário na tabela auth.users do Supabase com metadados para o trigger.
        const { data: authData, error: authError } = await supabase.auth.signUp({
            email: userData.email,
            password: userData.password,
            options: {
                data: {
                    username: userData.username,
                    full_name: userData.fullName,
                    birth_date: userData.birthDate,
                    gender: userData.gender
                }
            }
        });

        if (authError) {
            console.error('[Auth] Erro no signUp:', authError.message);
            return { success: false, message: this.translateAuthError(authError.message) };
        }
        
        if (!authData.user) {
             return { success: false, message: 'Não foi possível criar o usuário. Tente novamente.' };
        }

        // 2. Fallback de segurança: Caso o gatilho SQL no banco de dados não popule a tabela automaticamente,
        // tentamos fazer a atualização explicitamente (pode falhar se o e-mail exigir confirmação).
        const { error: profileError } = await supabase
            .from('profiles')
            .update({
                username: userData.username,
                full_name: userData.fullName,
                birth_date: userData.birthDate,
                gender: userData.gender
            })
            .eq('id', authData.user.id);
        
        if (profileError) {
            console.warn('[Auth] Aviso no signUp (fallback de perfil):', profileError.message);
        }

        return { success: true, message: 'Registro realizado! Verifique seu e-mail para confirmar a conta.' };
    },

    /**
     * Tenta autenticar um usuário com e-mail e senha.
     * @param {string} email - O e-mail do usuário.
     * @param {string} password - A senha do usuário.
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async signIn(email, password) {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            console.error('[Auth] Erro no signIn:', error.message);
            return { success: false, message: this.translateAuthError(error.message) };
        }

        return { success: true, message: 'Login realizado com sucesso!' };
    },

    /**
     * Faz o logout do usuário atual, invalidando a sessão.
     */
    async signOut() {
        const { error } = await supabase.auth.signOut();
        if (error) {
            console.error('[Auth] Erro no signOut:', error.message);
        }
        console.log('[Auth] Usuário deslogado.');
    },

    /**
     * Obtém a sessão do usuário atual do armazenamento local.
     * @returns {Promise<object|null>} A sessão do usuário ou null.
     */
    async getSession() {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
            console.error('[Auth] Erro ao obter sessão:', error.message);
            return null;
        }
        return session;
    },

    /**
     * Envia um e-mail de redefinição de senha para o e-mail fornecido.
     * @param {string} email - O e-mail para o qual enviar o link.
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async resetPasswordForEmail(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            // A URL de redirecionamento DEVE ser a raiz do site, sem o '#'.
            // O Supabase adicionará o token na hash.
            redirectTo: `${window.location.origin}/`,
        });

        if (error) {
            console.error('[Auth] Erro ao solicitar redefinição de senha:', error.message);
            return { success: false, message: this.translateAuthError(error.message) };
        }
        return { success: true, message: 'Se o e-mail estiver cadastrado, um link de redefinição foi enviado.' };
    },

    /**
     * Atualiza a senha do usuário atualmente logado (geralmente após chegar pelo link de redefinição).
     * @param {string} newPassword - A nova senha a ser definida.
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async updateUserPassword(newPassword) {
        const { error } = await supabase.auth.updateUser({
            password: newPassword,
        });

        if (error) {
            console.error('[Auth] Erro ao atualizar senha:', error.message);
            return { success: false, message: this.translateAuthError(error.message) };
        }
        return { success: true, message: 'Senha atualizada com sucesso!' };
    },
    
    /**
     * Registra um callback para ser executado sempre que o estado de autenticação mudar.
     * @param {function} callback - A função a ser chamada com (event, session).
     */
    onAuthStateChange(callback) {
        supabase.auth.onAuthStateChange((event, session) => {
            console.log(`[Auth] Estado de autenticação mudou. Evento: ${event}`);
            callback(event, session);
        });
    },

    /**
     * Traduz mensagens de erro comuns do Supabase para português.
     * @param {string} errorMessage - A mensagem de erro original.
     * @returns {string} A mensagem traduzida ou a original se não houver tradução.
     */
    translateAuthError(errorMessage) {
        if (errorMessage.includes('User already registered')) return 'Este e-mail já está registrado.';
        if (errorMessage.includes('Invalid login credentials')) return 'E-mail ou senha inválidos.';
        if (errorMessage.includes('Password should be at least 6 characters')) return 'A senha deve ter no mínimo 6 caracteres.';
        if (errorMessage.includes('Email not confirmed')) return 'Seu e-mail ainda não foi confirmado. Por favor, verifique sua caixa de entrada.';
        if (errorMessage.includes('For security purposes, you can only request a password reset once every')) return 'Aguarde um minuto antes de solicitar um novo link de redefinição.';
        if (errorMessage.includes('duplicate key value violates unique constraint "profiles_username_key"')) return 'Este nome de usuário já está em uso. Por favor, escolha outro.';
        return errorMessage;
    },

    /**
     * Helper para proteger rotas de admin. Verifica se o usuário está logado e tem a role 'admin'.
     * Redireciona automaticamente se a verificação falhar.
     * @returns {Promise<{isAllowed: boolean, profile: object|null}>}
     */
    async checkAdminAuth() {
        const session = await this.getSession();
        if (!session || !session.user) {
            console.warn('[Auth] Acesso de Admin negado: Usuário não logado.');
            window.router.navigate('/login');
            return { isAllowed: false, profile: null };
        }

        const { data: profile, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .single();
        
        if (error || profile.role !== 'admin') {
            console.warn(`[Auth] Acesso de Admin negado: Role '${profile?.role}' insuficiente.`);
            window.router.navigate('/'); // Redireciona para a home por segurança
            return { isAllowed: false, profile: null };
        }

        console.log('[Auth] Acesso de Admin permitido.');
        return { isAllowed: true, profile };
    },

    showConfirmationModal
};

export default auth;